import { clamp } from '../core/math';
import type { DayNumber } from '../core/time';
import { capacity } from './capacity';
import { B } from './constants';
import { earthBalance } from './earthBalance';
import type {
  Capacity,
  EarthBalance,
  EtaSource,
  Plan,
  Resources,
  Simulation,
  Site,
  Task,
  TaskForecast,
} from './types';
import { addWork, buildWorkCurve, workAt } from './workCurve';

/** 計画上の進み具合（0〜1）。 */
export const planProgress = (t: Pick<Task, 'ts' | 'dur'>, T: DayNumber): number =>
  clamp((T - t.ts) / t.dur, 0, 1);

/**
 * 基準日までの実績から実効率 η を求める（初期の施工計画で 1 回だけ）。
 * 土工が終わっている、またはまだ始まっていない現場は 0.80。
 */
export function calibrate(
  s: Pick<Site, 'g' | 'gate' | 'area' | 'tasks' | 'earthIdx' | 'region'>,
  p0: Plan,
): { eta: number; etaSrc: EtaSource; achieved: number | null } {
  const eb = earthBalance(s, p0);
  const cap = capacity(p0, eb);
  const t = s.tasks[s.earthIdx];
  if (t && t.a > 0 && t.a < 1) {
    const cw = buildWorkCurve(s.region, p0);
    const wd = workAt(cw, B) - workAt(cw, t.ts);
    const achieved = (t.a * eb.Q) / Math.max(1, wd);
    return { eta: clamp(achieved / Math.max(1, cap.min), 0.35, 1.15), etaSrc: 'obs', achieved };
  }
  return { eta: 0.8, etaSrc: t && t.a >= 1 ? 'done' : 'prior', achieved: null };
}

/** 実効施工量（地山 m³/稼働日）。1 未満にしない。 */
const effectiveRate = (eta: number, cap: Capacity): number => Math.max(1, eta * cap.min);

/** 各工種の予測開始日 S と終了日 F を、工程の上から順に決める。 */
export function forecast(
  s: Pick<Site, 'tasks' | 'eta'>,
  eb: EarthBalance,
  cap: Capacity,
  cw: Float64Array,
): TaskForecast[] {
  const out: TaskForecast[] = [];
  const rate = effectiveRate(s.eta, cap);
  for (const t of s.tasks) {
    const pr = t.pred >= 0 ? out[t.pred] : undefined;
    const pt = t.pred >= 0 ? s.tasks[t.pred] : undefined;
    let S: number;
    let F: number;
    if (t.a >= 1) {
      S = t.ts;
      F = Math.min(t.te, B);
    } else if (t.a > 0) {
      S = t.ts;
      if (t.earth) F = addWork(cw, B, (eb.Q * (1 - t.a)) / rate);
      else {
        const pp = planProgress(t, B);
        const spi = pp > 0.05 ? clamp(t.a / pp, 0.5, 1.3) : 1;
        F = B + Math.ceil(((1 - t.a) * t.dur) / spi);
      }
    } else {
      S = Math.max(t.ts, B, pr && pt ? pr.F + (t.ts - pt.te) : t.ts);
      F = t.earth ? addWork(cw, S, eb.Q / rate) : S + t.dur;
    }
    if (pr && pt && t.a < 1) F = Math.max(F, pr.F + Math.min(30, Math.max(0, t.te - pt.te)));
    F = Math.max(F, S + 1);
    out.push({ S, F });
  }
  return out;
}

/** 土工の稼働日・ダンプ延べ台数・走行距離・燃料・CO₂（原単位はサンプル値）。 */
export function resources(
  s: Pick<Site, 'tasks' | 'earthIdx' | 'eta'>,
  p: Plan,
  eb: EarthBalance,
  cap: Capacity,
): Resources {
  const so = eb.so;
  const rate = effectiveRate(s.eta, cap);
  const t = s.tasks[s.earthIdx];
  const workdays = eb.Q / rate;
  const tripsExt = (eb.outLoose + eb.borrowLoose) / p.cap;
  const tripsInt = (Math.max(0, eb.Q - eb.extNat) * so.L) / p.cap;
  const truckKm = tripsExt * 2 * p.dExt + (tripsInt * 2 * cap.dInt) / 1000;
  const fuel = workdays * p.hours * (p.ex * 15 + p.rollers * 8) + truckKm / 3.5;
  return {
    rate,
    workdays,
    remWork: workdays * (1 - (t ? t.a : 0)),
    tripsExt,
    tripsInt,
    truckKm,
    fuel,
    co2: (fuel * 2.58) / 1000,
    dailyExt: (rate * eb.extFrac * so.L) / p.cap,
  };
}

/** 施工計画 p での予測。現場を書きかえない。 */
export function simulate(s: Site, p: Plan): Simulation {
  const eb = earthBalance(s, p);
  const cap = capacity(p, eb);
  const cw = buildWorkCurve(s.region, p);
  const fc = forecast(s, eb, cap, cw);
  const end = Math.max(...fc.map((x) => x.F));
  return {
    p,
    eb,
    cap,
    cw,
    fc,
    end,
    delay: end - s.planEnd,
    earthEnd: s.earthIdx >= 0 ? fc[s.earthIdx]!.F : null,
    res: resources(s, p, eb, cap),
  };
}

/** 交通誘導員の目安（名）。場外ダンプの往復が 60 台/日、120 台/日を超えるごとに 1 名足す。 */
export function trafficGuards(res: Pick<Resources, 'dailyExt'>): number {
  return 1 + (res.dailyExt * 2 > 60 ? 1 : 0) + (res.dailyExt * 2 > 120 ? 1 : 0);
}
